import React, { useEffect, useState } from 'react';
import intl from '$intl';
import { IonContent, IonFooter } from '@ionic/react';
import { TextField } from '@mui/material';
import styled from 'styled-components';

import { SPACE } from '$gstyles';
import { Flex, PageWrapper } from '$gstyles/wrapper';
import { Button } from '$gcomponents/primitives';
import { Modal } from '$gcomponents/reusables';

import { verifyFileAccess } from '$fbusiness/redux/file/actions';
import FileModel from '$fbusiness/models/file';

const ModalWrapper = styled(Modal)`
  .file-name {
    opacity: 0.7;
    font-size: 0.9em;
    margin-bottom: 12px;
  }
  .error {
    color: red;
    font-size: 0.85em;
    margin-top: 8px;
  }
`;

interface Props {
  file: FileModel | null;
  show: boolean;
  onClose: () => void;
}

const PasscodeDialog: React.FC<Props> = ({ file, show, onClose }) => {
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset state every time the dialog opens for a new file.
  useEffect(() => {
    if (show) {
      setPasscode('');
      setError('');
      setSubmitting(false);
    }
  }, [show, file?.id]);

  const handleSubmit = async () => {
    if (!file || !passcode.trim()) return;
    setSubmitting(true);
    setError('');
    const result = await verifyFileAccess(file.id, passcode.trim());
    setSubmitting(false);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    // Hand off to the browser — opens in a new tab; browser handles the download.
    window.open(result.fileUrl, '_blank');
    onClose();
  };

  return (
    <ModalWrapper
      title="SCREEN.FILES.COLS.ENTER_PASSCODE"
      show={show}
      onClose={onClose}
      useCustom
      width="400px">
      <IonContent>
        <PageWrapper>
          {file?.fileName && <div className="file-name">{file.fileName}</div>}
          <TextField
            autoFocus
            fullWidth
            type="password"
            variant="outlined"
            size="small"
            autoComplete="off"
            placeholder={intl('SCREEN.FILES.COLS.PASSCODE')}
            value={passcode}
            disabled={submitting}
            onChange={(e) => setPasscode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSubmit();
            }}
          />
          {error && <div className="error">{error}</div>}
        </PageWrapper>
      </IonContent>
      <IonFooter>
        <Flex padding={SPACE.MEDIUM} justifyContent="center" gap="8px">
          <Button color="info" className="half" onClick={onClose}>
            {intl('BUTTON.CLOSE')}
          </Button>
          <Button
            className="half"
            onClick={handleSubmit}
            disabled={submitting || !passcode.trim()}>
            {intl('BUTTON.SUBMIT')}
          </Button>
        </Flex>
      </IonFooter>
    </ModalWrapper>
  );
};

export default PasscodeDialog;
